import React, { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Prompt } from 'react-router';
import { useLocation } from 'react-router-dom';
import styledUpdateSubmission from './styledUpdateSubmission';
import { ALERT_TEXT } from './UpdateSubmission.types';
import { SubmissionContext } from '../Context/SubmissionProvider';
import DetailsStep from './DetailsStep';
import BaseInfoStep from './BaseInfoStep';
import LegalStep from './LegalStep';
import ReleasePath from '../ReleasePath/ReleasePath';
import FlowType from '../GameBaseForm/FlowType';
import { StyledHeaderText } from '../../../../../../common/Styleguide/Common/Text';
import { Container } from '@mui/system';
import SubmissionOverview from './SubmissionOverview/SubmissionOverview';
import InfoUpdate from './InfoUpdate';
import SubmitNewVersion from './SubmitNewVersion';
import { isDefined } from '@crazygames/utils/isDefined';
import { goToQA } from './helpers';

const Root = styled('div')(styledUpdateSubmission);

export type UpdateSubmissionStep = 'baseInfo' | 'details' | 'legal' | 'overview' | 'infoUpdate' | 'gameBuild';

const UpdateSubmission: React.FC = () => {
  const { submission, gameBuild, modified } = useContext(SubmissionContext);
  const location = useLocation();
  const [activeStep, setActiveStep] = useState<UpdateSubmissionStep | undefined>();

  useEffect(() => {
    // Display alert when user tries to leave the page (quit/refresh) with unsaved changes
    // Complementary to the Prompt component, that only works when navigating within the app
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (modified) {
        event.preventDefault();
        event.returnValue = ALERT_TEXT;
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [modified]);

  useEffect(() => {
    if (isDefined(activeStep)) {
      return;
    }

    const getInitialActiveStep = (fromQA = false): UpdateSubmissionStep => {
      if (fromQA && submission?.status !== 'DRAFT' && submission?.isFullLaunch && submission.legalFormStatus !== 'FULL_INFO_COMPLETED') {
        // this will ask submissions that just transitioned from basic to full launch to fill the legal form again after returning from QA
        return 'legal';
      }
      if (submission?.status !== 'DRAFT') {
        return 'overview';
      }
      if (fromQA) {
        return 'details';
      }
      return 'baseInfo';
    };

    const paramsFromUrl = new URLSearchParams(location.search);
    const fromQA = !!submission && !!gameBuild && paramsFromUrl.get('fromQA') === 'true' && !!gameBuild.qaResult;
    setActiveStep(getInitialActiveStep(fromQA));

    if (fromQA) {
      // 'fromQA' is consumed in this route to know if the user should be redirect to the legal step for full launch
      // it should be cleaned however once checked, to avoid page refresh from sending you to the legal step
      const url = new URL(window.location.href);
      url.searchParams.delete('fromQA');
      window.history.replaceState({}, '', url);
    }
  }, [location.search, submission, gameBuild, activeStep]);

  if (!submission) {
    return null;
  }

  const goToLegal = () => {
    setActiveStep('legal');
  };

  const goToDetails = () => {
    setActiveStep('details');
  };

  const goToInfoUpdate = () => {
    setActiveStep('infoUpdate');
  };

  const goToOverview = () => {
    setActiveStep('overview');
  };

  const createNewGameBuild = () => {
    setActiveStep('gameBuild');
  };

  return (
    <Container sx={{ padding: 2 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <StyledHeaderText variant="h2">
            {activeStep === 'baseInfo' && 'Upload your game'}
            {activeStep === 'details' && 'Game details'}
            {activeStep === 'legal' && 'Finalize submission'}
            {activeStep === 'overview' && `Game overview - ${submission?.gameName}`}
          </StyledHeaderText>
          <FlowType submission={submission} />
        </div>
        {!submission.gameJamId && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {activeStep === 'baseInfo' && <ReleasePath step="upload" />}
            {activeStep === 'details' && <ReleasePath step="details" />}
            {activeStep === 'legal' && <ReleasePath step="submit" />}
          </div>
        )}
      </div>
      <Prompt when={modified} message={ALERT_TEXT} />
      <Root>
        {/* When updating a draft submission: */}
        {activeStep === 'baseInfo' && <BaseInfoStep goToQA={goToQA} goToDetails={goToDetails} />}
        {/* When arriving from "QA continue" (which uses /qa-continue/) for step 3: */}
        {activeStep === 'details' && <DetailsStep goToLegal={goToLegal} />}
        {/* Step 4 (showed once when submitting the basic requirement submission and once when submitting the full requirements one): */}
        {activeStep === 'legal' && <LegalStep />}
        {/* Basic view for submitted games: */}
        {activeStep === 'overview' && <SubmissionOverview goToInfoUpdate={goToInfoUpdate} createNewGameBuild={createNewGameBuild} />}
        {/* When updating the info of a game: */}
        {activeStep === 'infoUpdate' && <InfoUpdate goToOverview={goToOverview} goToQA={goToQA} />}
        {/* When submitting a new game version: */}
        {activeStep === 'gameBuild' && <SubmitNewVersion goToOverview={goToOverview} goToQA={goToQA} />}
      </Root>
    </Container>
  );
};

export default UpdateSubmission;
